import React, { useMemo, useState } from "react"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"

const InvoiceTypeModal = ({ isOpen, onClose, standardInvoicing }) => {
  const types       = standardInvoicing?.types || []
  const remaining   = Number(standardInvoicing?.remaining_invoiceable_percentage) || 100
  const totalAmount = (standardInvoicing?.quote_subtotal?.cents || 0) / 100

  const [selected, setSelected]     = useState(null)
  const [percentage, setPercentage] = useState("")

  const selectedType    = types.find(type => type.value === selected)
  const needsPercentage = !!selectedType?.needs_percentage

  const chooseType = type => {
    setSelected(type.value)
    if (type.needs_percentage) {
      setPercentage(String(standardInvoicing.deposit_default_percentage || Math.round(remaining)))
    }
  }

  const amount = useMemo(() => (totalAmount * (parseFloat(percentage) || 0)) / 100, [percentage, totalAmount])

  const clampPercentage = value => {
    const parsed = parseFloat(value)
    if (isNaN(parsed)) return ""
    return String(Math.max(0, Math.min(remaining, parsed)))
  }

  const onAmountChange = value => {
    const parsed = parseFloat(value) || 0
    const pct    = totalAmount > 0 ? (parsed / totalAmount) * 100 : 0
    setPercentage(clampPercentage(Math.round(pct * 100) / 100))
  }

  const submit = () => {
    if (!selectedType) {
      flashAlert(I18n.t("projects.invoices.type_modal.select_required"))
      return
    }
    if (selectedType.target === "situation") {
      if (standardInvoicing.situation_url) window.location.href = standardInvoicing.situation_url
      return
    }
    const url = new URL(standardInvoicing.new_invoice_url, window.location.origin)
    url.searchParams.set("type", selectedType.value)
    if (needsPercentage) url.searchParams.set("invoice_percentage", percentage || "0")
    window.location.href = url.pathname + url.search
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
        <Button variant="primary" onClick={submit} disabled={!selected}>{I18n.t("projects.invoices.type_modal.confirm")}</Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("projects.invoices.type_modal.eyebrow")}
      title   = {I18n.t("projects.invoices.type_modal.title")}
      size    = "md"
      footer  = {footer}
    >
      <div className="space-y-1.5">
        {types.map(type => (
          <label
            key       = {type.value}
            className = {`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
              selected === type.value ? "border-blue-400 bg-blue-50/50 ring-1 ring-blue-200" : "border-slate-200 hover:bg-slate-50"
            }`}
          >
            <input
              type      = "radio"
              name      = "invoice_type"
              checked   = {selected === type.value}
              onChange  = {() => chooseType(type)}
              className = "h-4 w-4"
            />
            <span className="text-[13px] text-slate-800">{type.label}</span>
          </label>
        ))}
      </div>

      {needsPercentage && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <input
            type      = "range"
            min       = "0"
            max       = {remaining}
            step      = "1"
            value     = {parseFloat(percentage) || 0}
            onChange  = {event => setPercentage(clampPercentage(event.target.value))}
            className  = "w-full"
          />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">{I18n.t("quotes.invoice.percentage")} %</label>
              <input
                type      = "number"
                min       = "0"
                max       = {remaining}
                step      = "0.01"
                value     = {percentage}
                onChange  = {event => setPercentage(clampPercentage(event.target.value))}
                className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm text-right tabular-nums"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">{I18n.t("quotes.invoice.amount")}</label>
              <input
                type      = "number"
                min       = "0"
                step      = "0.01"
                value     = {Math.round(amount * 100) / 100}
                onChange  = {event => onAmountChange(event.target.value)}
                className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm text-right tabular-nums"
              />
            </div>
          </div>
          <div className="mt-2 text-[11.5px] text-slate-500 text-right space-y-0.5">
            <div>{I18n.t("quotes.invoice.quote_total")} : {standardInvoicing?.quote_subtotal?.formatted}</div>
            <div>{I18n.t("quotes.invoice.already_invoiced")} : {standardInvoicing?.subtotal_invoiced?.formatted}</div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default InvoiceTypeModal
